import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { RoomScope } from "@/components/extranet/RatePlanDialog";
import { PROMO_TYPES } from "@/components/extranet/PromoTypes";
import { http, errMsg, COUNTRIES, todayISO, addDays } from "@/lib/api";

const base = (kind) => ({
  name: PROMO_TYPES[kind]?.title || "", kind, discount_percent: 10, room_type_ids: [], rate_plan_ids: [], stay_start: "", stay_end: "",
  booking_start: ["campaign", "flash_sale"].includes(kind) ? todayISO() : "", booking_end: ["campaign", "flash_sale"].includes(kind) ? addDays(todayISO(), 7) : "",
  min_nights: 1, last_minute_days: kind === "last_minute" ? 3 : null, early_bird_days: kind === "early_bird" ? 30 : null, countries: [], code: "", active: !["campaign", "flash_sale"].includes(kind),
});

const DateField = ({ label, value, onChange, testid }) => (
  <div><Label>{label}</Label><Input data-testid={testid} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" /></div>
);

export default function PromotionDialog({ open, onOpenChange, kind, promo, rooms, plans, propertyId, onSaved, bulkIds }) {
  const [f, setF] = useState(base(kind));
  const [campaigns, setCampaigns] = useState([]);
  useEffect(() => { if (open) http.get("/extranet/featured-campaigns").then((r) => setCampaigns(r.data.filter((c) => c.active))).catch(() => {}); }, [open]);
  useEffect(() => { if (open) setF(promo ? { ...base(promo.kind), ...promo } : base(kind)); }, [open, promo, kind]);
  const k = f.kind;
  const t = PROMO_TYPES[k];
  const set = (key) => (v) => setF({ ...f, [key]: v });
  const needBooker = ["campaign", "flash_sale"].includes(k);
  const sample = 1000000 * (1 - (Number(f.discount_percent) || 0) / 100);

  const save = async () => {
    try {
      if (bulkIds) {
        const { data } = await http.post("/extranet/promotions/bulk", { property_ids: bulkIds, promo: f });
        if (data.created.length) toast.success(`Promosi dibuat di ${data.created.length} properti`);
        data.failed.forEach((x) => toast.error(`${x.name}: ${x.error}`));
        onOpenChange(false); onSaved(data);
        return;
      }
      if (promo) await http.put(`/extranet/promotions/${promo.id}`, f);
      else await http.post(`/extranet/properties/${propertyId}/promotions`, f);
      toast.success("Promosi disimpan");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!t) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="extranet max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="promotion-dialog">
        <DialogHeader><DialogTitle>{promo ? "Edit" : "Tambah"} promosi — {t.title}</DialogTitle>
          {bulkIds && <p className="text-sm text-slate-500" data-testid="promo-bulk-note">{`Promo massal untuk ${bulkIds.length} properti · berlaku untuk semua tipe kamar & rate plan`}</p>}</DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Nama promosi (internal)</Label><Input data-testid="promo-name" value={f.name} onChange={(e) => set("name")(e.target.value)} className="mt-1.5" /></div>
          <div><Label>Diskon (%)</Label><Input data-testid="promo-discount" type="number" min={1} max={90} value={f.discount_percent} onChange={(e) => set("discount_percent")(Number(e.target.value))} className="mt-1.5" />
            <p className="mt-1 text-xs text-slate-500">Contoh: Rp 1.000.000 → Rp {Math.round(sample).toLocaleString("id-ID")}</p></div>

          {k === "last_minute" && <div><Label>Dipesan maksimal H- berapa hari sebelum check-in</Label><Input data-testid="promo-lastminute-days" type="number" min={0} value={f.last_minute_days ?? ""} onChange={(e) => set("last_minute_days")(Number(e.target.value))} className="mt-1.5" /></div>}
          {k === "early_bird" && <div><Label>Dipesan minimal H- berapa hari sebelum check-in</Label><Input data-testid="promo-earlybird-days" type="number" min={1} value={f.early_bird_days ?? ""} onChange={(e) => set("early_bird_days")(Number(e.target.value))} className="mt-1.5" /></div>}
          {k === "promo_code" && <div><Label>Kode promo</Label><Input data-testid="promo-code" value={f.code || ""} onChange={(e) => set("code")(e.target.value.toUpperCase())} className="mt-1.5 font-mono-x uppercase" /></div>}
          <div><Label>Minimal malam</Label><Input data-testid="promo-min-nights" type="number" min={1} value={f.min_nights} onChange={(e) => set("min_nights")(Number(e.target.value))} className="mt-1.5" /></div>

          {k === "geo" && (
            <div className="sm:col-span-2"><Label>Negara tamu</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2 rounded-md border border-slate-200 p-3 sm:grid-cols-3" data-testid="promo-countries">
                {COUNTRIES.map(([c, n]) => (
                  <label key={c} className="flex items-center gap-2 text-sm">
                    <Checkbox data-testid={`promo-country-${c}`} checked={f.countries.includes(c)} onCheckedChange={(v) => set("countries")(v ? [...f.countries, c] : f.countries.filter((x) => x !== c))} /> {n}
                  </label>
                ))}
              </div></div>
          )}

          <div className="rounded-md bg-slate-50 p-3 sm:col-span-2">
            <p className="text-sm font-bold">Tanggal pemesanan (booker date){needBooker ? " — wajib" : " — opsional"}</p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <DateField label="Mulai" value={f.booking_start} onChange={set("booking_start")} testid="promo-booking-start" />
              <DateField label="Selesai" value={f.booking_end} onChange={set("booking_end")} testid="promo-booking-end" />
            </div>
          </div>
          <div className="rounded-md bg-slate-50 p-3 sm:col-span-2">
            <p className="text-sm font-bold">Tanggal menginap — opsional</p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <DateField label="Mulai" value={f.stay_start} onChange={set("stay_start")} testid="promo-stay-start" />
              <DateField label="Selesai" value={f.stay_end} onChange={set("stay_end")} testid="promo-stay-end" />
            </div>
          </div>
          {!bulkIds && <>
          <div className="sm:col-span-2"><Label>Tipe kamar</Label><RoomScope rooms={rooms} value={f.room_type_ids} onChange={set("room_type_ids")} testid="promo-rooms" /></div>
          <div className="sm:col-span-2"><Label>Rate plan</Label><RoomScope rooms={plans} value={f.rate_plan_ids} onChange={set("rate_plan_ids")} testid="promo-plans" /></div>
          </>}
          {!["promo_code", "member"].includes(k) && <div className="sm:col-span-2"><Label>Ikut campaign grup (landing page)</Label>
            <select data-testid="promo-featured-campaign" value={f.featured_campaign_id || ""} onChange={(e) => set("featured_campaign_id")(e.target.value || null)} className="mt-1.5 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
              <option value="">Tidak ikut</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
            <p className="mt-1 text-xs text-slate-500">Properti tampil di banner promo landing page selama promosi ini aktif.</p></div>}
          <div className="text-sm sm:col-span-2">
            <span className="font-bold">Bisa digabung dengan: </span>{t.stacks.length ? t.stacks.join(" · ") : <span className="font-semibold text-[#DC2626]">tidak bisa digabung</span>}
            <p className="text-xs text-slate-500">Diskon bertumpuk dihitung berurutan: 100 − 10% = 90, lalu − 10% = 81.</p>
          </div>
          <label className="flex items-center gap-3 text-sm"><Switch data-testid="promo-active" checked={f.active} onCheckedChange={set("active")} /> Aktif</label>
        </div>
        <DialogFooter><button data-testid="promo-save" onClick={save} className="rounded-md bg-[#006CE4] px-5 py-2 font-semibold text-white hover:bg-[#0052B4]">Simpan promosi</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
